import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { GetResult, Preferences } from '@capacitor/preferences';
import DeviceProtectedSharedPreferences
  from '@plugins/deviceProtectedSharedPreferences/deviceProtectedSharedPreferences.plugin';
import { PlatformService } from './platform.service';
import { LocalStorageService } from './local-storage.service';
import SharedKeychain from '@plugins/sharedKeychain/sharedKeychain.plugin';

@Injectable({
  providedIn: 'root',
})
export class TokenStoreService {
  private readonly platformService = inject(PlatformService);
  private readonly localStorage = inject(LocalStorageService);

  private readonly ACCESS: string = 'access_token';
  private readonly REFRESH: string = 'refresh_token';
  private readonly secured: boolean;
  private readonly tempTokenLength: number = 16;

  constructor() {
    this.secured = this.platformService.isDevice && this.platformService.isAndroid;
    void this.syncSharedKeychain();
  }

  public async getToken(): Promise<string | null> {
    try {
      const accessTokenResult: GetResult | null = this.secured
        ? await DeviceProtectedSharedPreferences.get({key: this.ACCESS})
        : (typeof window !== 'undefined') ? await Preferences.get({key: this.ACCESS}) : null;

      const storedToken = accessTokenResult?.value ?? null;
      if (storedToken || !this.shouldUseSharedKeychain()) {
        if (storedToken && this.shouldUseSharedKeychain()) {
          void this.syncSharedKeychain();
        }
        return storedToken;
      }

      const sharedTokens = await this.readSharedTokens();
      if (sharedTokens?.accessToken) {
        await Preferences.set({key: this.ACCESS, value: sharedTokens.accessToken});
      }
      if (sharedTokens?.refreshToken) {
        await Preferences.set({key: this.REFRESH, value: sharedTokens.refreshToken});
      }
      return sharedTokens?.accessToken ?? null;
    } catch (error) {
      console.error('getToken error:', error);
      return null;
    }
  }

  public async getRefreshToken(): Promise<string | null> {
    try {
      const refreshTokenResult: GetResult | null = this.secured
        ? await DeviceProtectedSharedPreferences.get({key: this.REFRESH})
        : (typeof window !== 'undefined') ? await Preferences.get({key: this.REFRESH}) : null;

      const storedToken = refreshTokenResult?.value ?? null;
      if (storedToken || !this.shouldUseSharedKeychain()) {
        if (storedToken && this.shouldUseSharedKeychain()) {
          void this.syncSharedKeychain();
        }
        return storedToken;
      }

      const sharedTokens = await this.readSharedTokens();
      if (sharedTokens?.accessToken) {
        await Preferences.set({key: this.ACCESS, value: sharedTokens.accessToken});
      }
      if (sharedTokens?.refreshToken) {
        await Preferences.set({key: this.REFRESH, value: sharedTokens.refreshToken});
      }
      return sharedTokens?.refreshToken ?? null;
    } catch (error) {
      console.error('getRefreshToken error:', error);
      return null;
    }
  }

  public async setTokens(accessToken: string, refreshToken: string): Promise<void> {
    try {
      this.secured
        ? await DeviceProtectedSharedPreferences.set({key: this.ACCESS, value: accessToken})
        : await Preferences.set({key: this.ACCESS, value: accessToken});

      this.secured
        ? await DeviceProtectedSharedPreferences.set({key: this.REFRESH, value: refreshToken})
        : await Preferences.set({key: this.REFRESH, value: refreshToken});

      if (this.shouldUseSharedKeychain()) {
        await SharedKeychain.setTokens({accessToken, refreshToken});
      }
    } catch (error) {
      console.error('setTokens error:', error);
    }
  }

  public async removeTokens(): Promise<void> {
    try {
      this.secured
        ? await DeviceProtectedSharedPreferences.remove({key: this.ACCESS})
        : await Preferences.remove({key: this.ACCESS});

      this.secured
        ? await DeviceProtectedSharedPreferences.remove({key: this.REFRESH})
        : await Preferences.remove({key: this.REFRESH});

      if (this.shouldUseSharedKeychain()) {
        await SharedKeychain.clearTokens();
      }
    } catch (error) {
      console.error('removeTokens error:', error);
    }
  }

  public getTempToken(): string {
    if (this.platformService.isSSR) return 'bot';

    let tempToken: string = this.localStorage.getItem('tempToken');

    if (!tempToken && !this.platformService.isSSR) {
      tempToken = this.generateTempToken(this.tempTokenLength);
      this.localStorage.setItem('tempToken', tempToken);
    }
    return tempToken;
  }

  private generateTempToken(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let tempToken: string = '';
    const randomValues = new Uint8Array(length);
    window.crypto.getRandomValues(randomValues);

    for (let i = 0; i < length; i++) {
      tempToken += charset.charAt(randomValues[i] % charset.length);
    }
    return tempToken;
  }

  private shouldUseSharedKeychain(): boolean {
    return this.platformService.isIos
      && Capacitor.getPlatform() === 'ios'
      && Capacitor.isNativePlatform()
      && Capacitor.isPluginAvailable('SharedKeychain');
  }

  private async readSharedTokens(): Promise<{ accessToken: string | null; refreshToken: string | null } | null> {
    if (typeof SharedKeychain?.getTokens !== 'function') {
      return null;
    }
    try {
      return await SharedKeychain.getTokens();
    } catch (error) {
      console.error('readSharedTokens error:', error);
      return null;
    }
  }

  private async syncSharedKeychain(): Promise<void> {
    if (!this.shouldUseSharedKeychain()) {
      return;
    }
    if (typeof SharedKeychain?.setTokens !== 'function') {
      return;
    }
    try {
      const accessTokenResult = await Preferences.get({key: this.ACCESS});
      const refreshTokenResult = await Preferences.get({key: this.REFRESH});
      const accessToken = accessTokenResult?.value ?? null;
      const refreshToken = refreshTokenResult?.value ?? null;
      if (accessToken || refreshToken) {
        await SharedKeychain.setTokens({accessToken, refreshToken});
      }
    } catch (error) {
      console.error('syncSharedKeychain error:', error);
    }
  }
}
